// 2. api.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import { phpClient } from "../../../network";
import {
    AddNotesApiResponse,
    AddNotesPayload,
    AddTimecardApiResponse,
    AddTimecardPayload,
    AdjustTimeCardPayload,
    AdjustTimeCardResponse,
    GetAuditApiResponse,
    GetAuditPayload,
    GetNotesApiResponse,
    GetNotesPayload,
    GetTimeCardPayload,
    GetTimeCardSummaryPayload,
    GroupedTimeCardApiResponse,
    GroupedTimeCardPayload,
    TimeCardApiResponse,
    TimecardRecordDayDetails,
    TimeCardSummaryApiResponse,
} from "../../../types/state/timecards";
import { getStartAndEndOfWeek, getTodayAtMidnight, getTomorrowAtMidnight } from "../../../utils/employeeTimeCardhelper";

export const getEmployeeTimeCard = createAsyncThunk(
    "timecards/employeeTimecard",
    async (payload: GetTimeCardPayload, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<TimeCardApiResponse>({
                url: "/timecard",
                data: payload,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getEmployeeTimeCardSummary = createAsyncThunk(
    "timecards/employeeTimecardSummary",
    async (payload: GetTimeCardSummaryPayload = {}, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<TimeCardSummaryApiResponse>({
                url: "/timecard/get_summary",
                data: payload,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Create Async Thunk for /timecard/grouped API call
export const getEmployeeTimeCardGrouped = createAsyncThunk(
    "timecards/employeeTimecardGrouped",
    async (payload: GroupedTimeCardPayload = {}, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<GroupedTimeCardApiResponse>({
                url: "/timecard/grouped",
                data: payload,
            });
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getTodayTimeCardSummary = createAsyncThunk(
    "timecards/todayEmployeeTimecardSummary",
    async (employee_id: number, { rejectWithValue }) => {
        try {
            const payload: GetTimeCardSummaryPayload = {
                employee_id,
                start_date: getTodayAtMidnight(),
                end_date: getTomorrowAtMidnight(),
            };
            const response = await phpClient.post<TimeCardSummaryApiResponse>({
                url: "/timecard/get_summary",
                data: payload,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// This Week's Summary
export const getThisWeekTimeCardSummary = createAsyncThunk(
    "timecards/thisWeekEmployeeTimecardSummary",
    async (employee_id: number, { rejectWithValue }) => {
        try {
            const { start, end } = getStartAndEndOfWeek();
            const payload: GetTimeCardSummaryPayload = {
                employee_id,
                start_date: start,
                end_date: end,
            };
            const response = await phpClient.post<TimeCardSummaryApiResponse>({
                url: "/timecard/get_summary",
                data: payload,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getEmployeeTimeCardRecordDetail = createAsyncThunk(
    "timecards/getEmployeeTimecardRecordDetail",
    async (data: { id: number; employee_id?: number }, thunkAPI) => {
        const { rejectWithValue } = thunkAPI;
        try {
            const { id, employee_id } = data;
            const requestBody: Record<string, any> = {
                extra_requested_fields: ["notes_count", "notes_details", "audit_count", "audit_details"],
            };
            if (employee_id) {
                requestBody.employee_id = employee_id;
            }

            const response = await phpClient.post<{ data: TimecardRecordDayDetails }>({
                url: `/timecard/details/${id}`,
                data: requestBody,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getAuditLogsApi = createAsyncThunk<GetAuditApiResponse, GetAuditPayload>(
    "timecards/getAuditLogs",
    async (payload: GetAuditPayload, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<GetAuditApiResponse>({
                url: "/timecard/get_audit",
                data: payload,
            });
            return response.data as GetAuditApiResponse; // Explicitly cast to the expected type
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const adjustTimeCardApi = createAsyncThunk<
    AdjustTimeCardResponse,
    AdjustTimeCardPayload,
    { rejectValue: string }
>("timecards/adjustTimeCard", async (payload: AdjustTimeCardPayload, { rejectWithValue }) => {
    try {
        const { record_id, ...restOfPayload } = payload;
        const response = await phpClient.post<AdjustTimeCardResponse>({
            url: `/timecard/adjust/${record_id}`,
            data: restOfPayload,
        });
        return response;
    } catch (error) {
        return rejectWithValue("Error adjusting timecard"); // Or you can return the error itself
    }
});

// Redux Thunk for Get Notes API
export const getNotesToTimecardRecord = createAsyncThunk<GetNotesApiResponse, GetNotesPayload>(
    "timecards/getNotesToTimecardRecord",
    async (payload: GetNotesPayload, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<GetNotesApiResponse>({
                url: "/timecard/get_notes",
                data: payload,
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

// Redux Thunk for Add Notes API
export const addNotesToTimecardRecord = createAsyncThunk<AddNotesApiResponse, AddNotesPayload>(
    "timecards/addNotesToTimecardRecord",
    async (payload: AddNotesPayload, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<AddNotesApiResponse>({
                url: "/timecard/add_notes",
                data: payload,
            });
            return response; // Explicitly cast to the expected type
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const addTimecardRecord = createAsyncThunk<AddTimecardApiResponse, AddTimecardPayload>(
    "timecards/addTimecardRecord",
    async (payload: AddTimecardPayload, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<AddTimecardApiResponse>({
                url: "/timecard/add",
                data: payload,
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred while adding timecard");
        }
    }
);
